import { CWY_COLORS } from '../../theme/colors';
import { AUTH_PRIMARY, AUTH_PRIMARY_HOVER } from '../auth/authFormStyles';

/** Account & vault design tokens — CWY maple red brand */
export const ACCOUNT_COLORS = {
  primary: AUTH_PRIMARY,
  primaryHover: AUTH_PRIMARY_HOVER,
  navy: CWY_COLORS.navy,
  navyHover: CWY_COLORS.navyLight,
  teal: CWY_COLORS.teal,
  tealMuted: 'rgba(0, 113, 133, 0.1)',
  bg: CWY_COLORS.pageBg,
  surface: 'var(--cwy-cardBg, #FFFFFF)',
  surfaceMuted: 'var(--cwy-pageBg, #F5F6F9)',
  surface2: 'var(--cwy-pageBg, #F5F6F9)',
  surface3: 'var(--cwy-border, #EBEDF2)',
  surfaceElevated: 'var(--cwy-cardBg, #FFFFFF)',
  pageGradient: 'var(--cwy-pageBg)',
  border: 'var(--cwy-border, #E1E4EA)',
  borderSoft: 'var(--cwy-border, #EAECF1)',
  borderStrong: 'var(--cwy-border, #CBD5E1)',
  text: 'var(--cwy-textPrimary, #1B2233)',
  textMuted: 'var(--cwy-textSecondary, #6B7186)',
  textSubtle: 'var(--cwy-textSecondary, #9BA0B2)',
  textHi: 'var(--cwy-textPrimary, #1B2233)',
  textLo: 'var(--cwy-textSecondary, #6B7186)',
  textDim: 'var(--cwy-textSecondary, #9BA0B2)',
  /** Maple leaf accent — replaces former gold treasury palette */
  gold: CWY_COLORS.red,
  goldBright: AUTH_PRIMARY,
  goldDim: 'rgba(204, 27, 27, 0.10)',
  success: '#1FA971',
  successDim: 'rgba(31,169,113,.10)',
  warning: '#B9791E',
  warningDim: 'rgba(224,165,55,.14)',
  danger: CWY_COLORS.priceRed,
  dangerHover: '#B91C1C',
  dangerBg: 'rgba(208,71,58,.10)',
  dangerBorder: 'rgba(208,71,58,.25)',
  walletAccent: 'linear-gradient(145deg, #FF4040, #CC0000)',
  walletGlow: '0 1px 2px rgba(20,24,40,.04), 0 8px 24px -16px rgba(20,24,40,.08)',
  fontDisplay: '"Space Grotesk", "Plus Jakarta Sans", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
  fontMono: '"IBM Plex Mono", ui-monospace, SFMono-Regular, Menlo, monospace',
  radius: '14px',
  radiusSm: '9px',
} as const;

export const accountCardSx = {
  bgcolor: ACCOUNT_COLORS.surface,
  border: `1px solid ${ACCOUNT_COLORS.borderSoft}`,
  borderRadius: ACCOUNT_COLORS.radius,
  p: 3,
  mb: 2,
  boxShadow: '0 1px 2px rgba(20,24,40,.04), 0 8px 24px -16px rgba(20,24,40,.08)',
} as const;

export const accountStatementSx = {
  position: 'relative',
  overflow: 'hidden',
  bgcolor: ACCOUNT_COLORS.surface,
  border: `1px solid ${ACCOUNT_COLORS.borderSoft}`,
  borderRadius: ACCOUNT_COLORS.radius,
  p: { xs: 2.5, sm: 3.25 },
  mb: 2,
  boxShadow: '0 1px 2px rgba(20,24,40,.04), 0 8px 24px -16px rgba(20,24,40,.08)',
} as const;

export const accountPageRootSx = {
  minHeight: '100%',
  width: '100%',
  bgcolor: ACCOUNT_COLORS.bg,
  display: 'flex',
  flexDirection: 'column',
} as const;

export const accountPageInnerSx = {
  width: '100%',
  maxWidth: 920,
  mx: 'auto',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  px: { xs: 2.5, sm: 2.5 },
  py: { xs: 2.5, sm: 3.5 },
  pb: { xs: 8, sm: 10 },
  boxSizing: 'border-box',
} as const;

export const accountPageHeaderSx = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: { xs: 'flex-start', sm: 'flex-end' },
  justifyContent: 'space-between',
  gap: 2,
  mb: { xs: 2.5, md: 3 },
  pb: { xs: 2, md: 2.5 },
  borderBottom: `1px solid ${ACCOUNT_COLORS.border}`,
} as const;

export const accountPageTitleSx = {
  fontFamily: ACCOUNT_COLORS.fontDisplay,
  fontWeight: 800,
  fontSize: { xs: '1.5rem', md: '1.75rem' },
  letterSpacing: '-0.03em',
  color: ACCOUNT_COLORS.text,
  lineHeight: 1.2,
  mb: 0.5,
} as const;

export const accountPageSubtitleSx = {
  fontFamily: ACCOUNT_COLORS.fontBody,
  fontSize: '0.9375rem',
  color: ACCOUNT_COLORS.textMuted,
  lineHeight: 1.5,
  maxWidth: 520,
} as const;

export const accountMainGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', lg: 'minmax(320px, 400px) minmax(0, 1fr)' },
  gap: { xs: 2.5, md: 3 },
  alignItems: 'start',
  flex: 1,
} as const;

export const accountSettingsStackSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: { xs: 2.5, md: 3 },
  minWidth: 0,
} as const;

export const accountSettingsGridSx = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
  gap: { xs: 2.5, md: 3 },
  alignItems: 'start',
} as const;

export const accountStickyColumnSx = {
  position: { lg: 'sticky' },
  top: { lg: 16 },
  alignSelf: 'start',
} as const;

export const accountWalletPrimaryColumnSx = {
  width: '100%',
  minWidth: 0,
} as const;

export const accountCompactSettingsBarSx = {
  p: { xs: 2, sm: 2.5 },
  borderRadius: ACCOUNT_COLORS.radius,
  border: `1px solid ${ACCOUNT_COLORS.borderSoft}`,
  bgcolor: ACCOUNT_COLORS.surface,
  boxShadow: '0 1px 2px rgba(20,24,40,.04), 0 8px 24px -16px rgba(20,24,40,.08)',
  mt: 2.5,
} as const;

export const accountHeroBannerSx = {
  width: '100%',
  borderRadius: { xs: '14px', md: '16px' },
  border: `1px solid ${ACCOUNT_COLORS.border}`,
  bgcolor: ACCOUNT_COLORS.surface,
  boxShadow: '0 4px 24px rgba(15, 23, 42, 0.06)',
  overflow: 'hidden',
  mb: { xs: 2.5, md: 3 },
  position: 'relative',
} as const;

export const accountHeroBannerTopSx = {
  background: ACCOUNT_COLORS.walletAccent,
  px: { xs: 2.5, sm: 3, md: 4 },
  py: { xs: 3, md: 3.5 },
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background:
      'radial-gradient(ellipse 80% 60% at 100% 0%, rgba(255,255,255,0.12) 0%, transparent 60%)',
    pointerEvents: 'none',
  },
} as const;

export const accountHeroBannerBodySx = {
  px: { xs: 2.5, sm: 3, md: 4 },
  py: { xs: 2.5, sm: 3 },
  bgcolor: ACCOUNT_COLORS.surface,
} as const;

export const accountSectionCardSx = {
  ...accountCardSx,
  transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
} as const;

export const accountWalletCardSx = {
  ...accountSectionCardSx,
} as const;

export const accountSectionTitleSx = {
  fontFamily: ACCOUNT_COLORS.fontDisplay,
  fontWeight: 700,
  fontSize: '1.0625rem',
  letterSpacing: '-0.02em',
  color: ACCOUNT_COLORS.text,
  mb: 0.35,
} as const;

export const accountSectionDescSx = {
  fontFamily: ACCOUNT_COLORS.fontBody,
  fontSize: '0.8125rem',
  color: ACCOUNT_COLORS.textMuted,
  lineHeight: 1.55,
  mb: 2,
} as const;

export const accountSectionIconCircleSx = {
  width: 40,
  height: 40,
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  bgcolor: ACCOUNT_COLORS.tealMuted,
  flexShrink: 0,
} as const;

export const accountReadOnlyBoxSx = {
  p: 2,
  borderRadius: '10px',
  bgcolor: ACCOUNT_COLORS.surfaceMuted,
  border: `1px solid ${ACCOUNT_COLORS.border}`,
} as const;

export const accountDialogFieldLabelSx = {
  fontFamily: ACCOUNT_COLORS.fontBody,
  fontSize: '0.8125rem',
  fontWeight: 600,
  color: ACCOUNT_COLORS.textMuted,
  mb: 0.75,
} as const;

export const accountFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: ACCOUNT_COLORS.surfaceMuted,
    fontFamily: ACCOUNT_COLORS.fontBody,
    '&:hover fieldset': { borderColor: ACCOUNT_COLORS.borderStrong },
    '&.Mui-focused fieldset': { borderColor: ACCOUNT_COLORS.primary, borderWidth: 2 },
  },
  '& .MuiInputLabel-root': {
    fontFamily: ACCOUNT_COLORS.fontBody,
    '&.MuiInputLabel-shrink': {
      bgcolor: ACCOUNT_COLORS.surface,
      px: 0.5,
    },
  },
} as const;

export const accountPhoneInputStyles = {
  inputStyle: {
    width: '100%',
    height: '44px',
    borderColor: ACCOUNT_COLORS.border,
    borderRadius: '10px',
    fontSize: '0.9375rem',
    backgroundColor: ACCOUNT_COLORS.surfaceMuted,
    fontFamily: ACCOUNT_COLORS.fontBody,
  },
  buttonStyle: {
    borderColor: ACCOUNT_COLORS.border,
    borderTopLeftRadius: '10px',
    borderBottomLeftRadius: '10px',
    backgroundColor: ACCOUNT_COLORS.surfaceMuted,
  },
  containerStyle: { width: '100%' },
} as const;

export const accountWalletAddressBoxSx = {
  p: 2,
  borderRadius: '10px',
  bgcolor: ACCOUNT_COLORS.surfaceMuted,
  border: `1px solid ${ACCOUNT_COLORS.border}`,
} as const;

/** @deprecated use accountPageRootSx + accountPageInnerSx */
export const accountPageBg = accountPageRootSx;

/** @deprecated use accountPageInnerSx */
export const accountContainerSx = { width: '100%', maxWidth: '100%' } as const;

/** @deprecated */
export const accountHeroCardSx = accountHeroBannerSx;

/** @deprecated */
export const accountPrimaryButtonSx = {
  py: 1.25,
  px: 2.5,
  borderRadius: '10px',
  fontWeight: 600,
  fontSize: '0.875rem',
  bgcolor: AUTH_PRIMARY,
  color: '#fff',
  boxShadow: 'none',
  textTransform: 'none',
  '&:hover': { bgcolor: AUTH_PRIMARY_HOVER, boxShadow: 'none' },
  '&:disabled': { bgcolor: '#E8EAED', color: '#9AA0A6' },
} as const;

/** @deprecated use AccountButton */
export const accountGhostButtonSx = {
  py: 1.25,
  px: 2,
  borderRadius: '10px',
  fontWeight: 600,
  fontSize: '0.875rem',
  color: ACCOUNT_COLORS.text,
  borderColor: ACCOUNT_COLORS.border,
  textTransform: 'none',
  '&:hover': { borderColor: ACCOUNT_COLORS.borderStrong, bgcolor: ACCOUNT_COLORS.surfaceMuted },
} as const;

export const accountTwoColumnGridSx = accountMainGridSx;

export const quickActionCardSx = {
  p: 2.5,
  borderRadius: '14px',
  border: `1px solid ${ACCOUNT_COLORS.border}`,
  bgcolor: ACCOUNT_COLORS.surface,
  cursor: 'pointer',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 28px rgba(15, 23, 42, 0.08)',
    borderColor: ACCOUNT_COLORS.borderStrong,
  },
} as const;
